import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { Typography } from '@material-ui/core';
import TraitTextCorpusCSVUploader from './TraitTextCorpusCSVUploader';
import TraitTextCorpusUploadPreview from './TraitTextCorpusUploadPreview';
import { useAlerts } from '../../../../hooks/alerts-hooks';
import axiosClient from '../../../../http/axios-client';
import AppButtonMain from '../../../basic/AppButtonMain';

const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => { return resolve({ width: i.width, height: i.height }); };
    i.onerror = (error) => { return reject(error); };
    i.src = file;
  });
};

const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => { return resolve(reader.result); };
    reader.onerror = (error) => { return reject(error); };
  });
};

const ImagesUpload = (
  {
    onImagesLoaded,
  },
) => {
  return (
    <input
      type='file'
      name='file'
      multiple
      accept='image/*'
      onChange={ (e) => {
        const fileListAsArray = Array.from(e.target.files);

        if (!Array.isArray(fileListAsArray) || 0 === fileListAsArray.length) {
          onImagesLoaded(null);
          return null;
        }

        const promises = fileListAsArray.map(async (file) => {
          const base64 = await toBase64(file);
          const size = await getImageDimensions(base64);
          return {
            name: file.name,
            type: file.type,
            base64,
            size,
          };
        });
        return Promise.all(promises)
          .then((base64array) => {
            onImagesLoaded(base64array);
          });
      } }
    />
  );
};

const TextCorpus = (
  {
    trait,
  },
) => {
  const [ data, setData ] = useState(null);
  const [ images, setImages ] = useState(null);

  const { alertSuccess, alertError } = useAlerts();

  const dataWithTrait = useMemo(() => {
    if (data === undefined || null === data) {
      return data;
    }
    const newData = { ...data };
    newData.rows = newData.rows.map((r) => {
      return [
        ...r,
        {
          Key: 'trait',
          Value: trait.name,
        },
      ];
    });
    newData.columns = [ ...newData.columns, {
      id: newData.columns.length,
      label: 'trait',
      field: 'trait',
    }];

    return newData;
  }, [ data, trait ]);

  // Names of the images to be uploaded
  const imageNames = useMemo(() => {
    if (data === undefined || null === data) {
      return [];
    }

    const tempImageNames = data.rows
      .filter((row) => {
        const e = row.find((entry) => { return 'token' === entry.Key; });
        const hasImage = e?.Value.startsWith('IMG_') || e?.Value?.split('.')?.[2]?.startsWith('IMG_');
        return !!hasImage;
      })
      .map((row) => {
        const imageName = row.find((entry) => { return 'content' === entry.Key; })?.Value;
        return imageName;
      });

    const uniqImageNames = [ ...new Set(tempImageNames) ];
    return uniqImageNames;
  }, [ data, trait ]);

  const {
    mutateAsync: uploadCorpus,
    isLoading: corpusIsUploading,
  } = useMutation(
    [ 'text-corpus-upload' ],
    (payload) => {
      return axiosClient.post('text-corpus-upload', payload);
    },
  );

  const imagesNeedToBeUploaded = useMemo(() => {
    if (!Array.isArray(imageNames) || 0 === imageNames.length) {
      return [];
    }
    if (!Array.isArray(images)) {
      return imageNames;
    }
    const uploadedImageNames = images.map((file) => {
      return file.name;
    });
    return imageNames.filter((imageName) => {
      return !uploadedImageNames.includes(imageName);
    });
  }, [ imageNames, images ]);

  const dataWithImagesAndTrait = useMemo(() => {
    if (!images) {
      return dataWithTrait;
    }

    if (!dataWithTrait) {
      return dataWithTrait;
    }

    const newData = { ...dataWithTrait };
    newData.rows = newData.rows.map((row) => {
      const e = row.find((entry) => { return 'token' === entry.Key; });
      if (!e || !e.Value) {
        return [ ...row ];
      }

      const hasImage = e.Value.startsWith('IMG_') || e.Value.split('.')?.[2]?.startsWith('IMG_');

      if (!hasImage) {
        return [ ...row ];
      }

      const contentIndex = row.findIndex((entry) => {
        return 'content' === entry.Key;
      });

      if (-1 === contentIndex) {
        return [ ...row ];
      }

      const contentEntry = row[contentIndex];

      const contentImage = images.find((imgFile) => {
        return imgFile.name === contentEntry.Value;
      });

      if (!contentImage) {
        return [ ...row ];
      }

      return [
        ...row.slice(0, contentIndex),
        {
          ...contentEntry,
          Value: contentImage,
        },
        ...row.slice(contentIndex + 1),
      ];
    });

    return newData;
  }, [ dataWithTrait, images ]);

  const ctaIsDisabled = useMemo(() => {
    if (!data) {
      return true;
    }

    if (corpusIsUploading) {
      return true;
    }

    if (!imagesNeedToBeUploaded) {
      return false;
    }

    return 0 < imagesNeedToBeUploaded.length;
  }, [ imagesNeedToBeUploaded, corpusIsUploading ]);

  const handleUpload = () => {
    /**
     * this is the most important transformation
     */
    const simpleTokenRegex = /^(?!IMG_)[A-Z](_?[A-Z])*$/;
    const imageTokenRegex = /^IMG_[A-Z](_?[A-Z])*$/;
    const arrayRegex = /^(?!IMG_)[A-Z](_?[A-Z])*\.[1-9]\d*\.[A-Z](_?[A-Z])*$/;

    const filterRegex = (dataR, rgx) => {
      return dataR.filter((row) => {
        const token = row.find((entry) => { return 'token' === entry.Key; });
        if (!token) {
          return false;
        }
        return rgx.test(token.Value);
      });
    };

    const simpleTokens = filterRegex(dataWithImagesAndTrait.rows, simpleTokenRegex);
    const imageTokens = filterRegex(dataWithImagesAndTrait.rows, imageTokenRegex);
    const arrayTokens = filterRegex(dataWithImagesAndTrait.rows, arrayRegex);

    const simpleTokensTransformed = simpleTokens
      .map((row) => {
        return row.reduce((acc, entry) => {
          let value;
          if ('content' === entry.Key) {
            value = {
              type: 'STRING',
              value: entry.Value,
            };
          } else {
            value = entry.Value;
          }
          acc[entry.Key] = value;
          return acc;
        }, {});
      });

    const imageTokensTransformed = imageTokens
      .map((row) => {
        return row.reduce((acc, entry) => {
          let value;
          if ('content' === entry.Key) {
            value = {
              type: 'IMAGE',
              value: entry.Value,
            };
          } else {
            value = entry.Value;
          }
          acc[entry.Key] = value;
          return acc;
        }, {});
      });

    // trait, category, race, locale,
    const arrayContextDict = {};
    arrayTokens.forEach((row) => {
      const { token, content, trait: traitR, category, race, locale } = row.reduce((acc, entry) => {
        acc[entry.Key] = entry.Value;
        return acc;
      }, {});

      const [ basicToken, position, subToken ] = token.split('.');
      const contextHash = JSON.stringify([ basicToken, traitR, category, race, locale ]);
      if (!arrayContextDict[contextHash]) {
        arrayContextDict[contextHash] = {
          token: basicToken,
          trait: traitR,
          category,
          race,
          locale,
          content: [],
        };
      }
      const pos = parseInt(position, 10) - 1;

      if (!arrayContextDict[contextHash].content[pos]) {
        arrayContextDict[contextHash].content[pos] = {};
      }

      arrayContextDict[contextHash].content[pos][subToken] = {
        type: subToken.startsWith('IMG_') ? 'IMAGE' : 'STRING',
        value: content,
      };
    });

    const arrayTokensTransformed = Object.values(arrayContextDict)
      .map((v) => {
        return {
          ...v,
          content: {
            type: 'ARRAY',
            value: v.content,
          },
        };
      });

    const tokenContentContextEntries = [ ...simpleTokensTransformed, ...imageTokensTransformed, ...arrayTokensTransformed ];
    return uploadCorpus(tokenContentContextEntries)
      .then(() => {
        alertSuccess('Texts were successfully uploaded');
        setData(null);
      })
      .catch((err) => {
        alertError(JSON.stringify(err));
      });
  };

  return (
    <>
      <TraitTextCorpusCSVUploader setDataCSV={ setData } />
      <br />
      <TraitTextCorpusUploadPreview dataCSV={ data } />
      <br />
      <ImagesUpload onImagesLoaded={ setImages } />
      <br />
      {0 < imagesNeedToBeUploaded.length && (
        <Typography variant='subtitle2'>
          In order to proceed you need to upload the following images:
          {' '}
          {imageNames.join(', ')}
        </Typography>
      )}
      <br />
      <AppButtonMain
        text={ corpusIsUploading ? 'Uploading' : 'Upload' }
        disabled={ ctaIsDisabled }
        onClick={ () => { return handleUpload(); } }
      />
    </>
  );
};

export default TextCorpus;
